import React, { useState, useEffect } from 'react';
import { post } from '../services/http-service';
import {
	Button,
	Box,
	InputLabel,
	Select,
	Dialog,
	ListItemText,
	MenuItem,
	OutlinedInput,
	FormControl,
	Typography,
	CircularProgress,
	Paper,
	Divider,
	IconButton,
	Tooltip,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import storageService from 'src/services/storage-service';
import Alert from '@mui/material/Alert';
import DialogClose from './DialogClose';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const FIELDS = [
	{
		key: 'company',
		label: 'Kommun/företag',
		required: true,
		value: '',
		type: 'text',
	},
	{
		key: 'email',
		label: 'E-postadress',
		required: true,
		value: '',
		type: 'email',
	},
	{ key: 'unit', label: 'Förvaltning/enhet', value: '', type: 'text' },
	{ key: 'name', label: 'Namn', value: '', type: 'text' },
	{ key: 'phone', label: 'Telefon', value: '', type: 'tel' },
];

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SocratesOfferDialog({ open, onClose }) {
	const [errorMessage, setErrorMessage] = useState('');
	const [fields, setFields] = useState(FIELDS);
	const [sent, setSent] = useState(false);
	const [loading, setLoading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});

	useEffect(() => {
		setErrorMessage('');
		setFieldErrors({});
		const user = storageService.load();
		FIELDS[0].value = user.organizationName;
		FIELDS[1].value = user.email.indexOf('@') > 0 ? user.email : '';
		FIELDS[3].value = user.displayName;
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	const validateField = (field, value) => {
		if (field.required && !value.trim()) {
			return `${field.label} är obligatoriskt`;
		}
		if (field.type === 'email' && value && !EMAIL_REGEX.test(value)) {
			return 'Ange en giltig e-postadress';
		}
		if (field.type === 'tel' && value && !/^[\d\s\-\+\(\)]+$/.test(value)) {
			return 'Ange ett giltigt telefonnummer';
		}
		return '';
	};

	const changeField = (index, value) => {
		const newFields = [...fields];
		newFields[index].value = value;
		setFields(newFields);

		// Don't show errors while typing - only clear them if they exist
		if (fieldErrors[index]) {
			setFieldErrors((prev) => ({
				...prev,
				[index]: '',
			}));
		}
	};

	const handleFieldBlur = (index) => {
		const field = fields[index];
		const error = validateField(field, field.value);
		setFieldErrors((prev) => ({
			...prev,
			[index]: error,
		}));
	};

	const validateForm = () => {
		const errors = {};
		let hasErrors = false;

		fields.forEach((field, index) => {
			const error = validateField(field, field.value);
			if (error) {
				errors[index] = error;
				hasErrors = true;
			}
		});

		setFieldErrors(errors);
		return !hasErrors;
	};

	const onSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);
		setErrorMessage('');

		try {
			const form = new FormData();
			form.append('product.name', 'Socrates');

			fields.forEach((f) => {
				form.append(f.key, f.value);
			});

			await post('/quote/new', form, true);
			setSent(true);
		} catch (error) {
			setErrorMessage(
				'Ett fel uppstod. Försök igen eller kontakta oss direkt.'
			);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			onClose();
		}
	};

	if (!open) {
		return null;
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			scroll="paper"
			aria-labelledby="scroll-dialog-title"
			aria-describedby="scroll-dialog-description"
			maxWidth="sm"
			fullWidth
			sx={{
				marginBottom: '24px',
			}}
		>
			<DialogTitle
				id="scroll-dialog-title"
				sx={{
					borderBottom: '1px solid #e0e0e0',
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					backgroundColor: '#f8f9fa',
				}}
			>
				<Typography variant="h6" component="span">
					Boka demo av Socrates
				</Typography>
			</DialogTitle>

			<DialogClose onClose={handleClose} />

			{sent ? (
				<DialogContent>
					<Box
						component="img"
						src="/Socrates_ikon.png"
						alt="Socrates"
						sx={{
							width: 28,
							height: 28,
							objectFit: 'contain',
						}}
					/>
					<Box textAlign="center" py={3}>
						<CheckCircleIcon
							color="success"
							sx={{ fontSize: 64, mb: 2 }}
						/>
						<Typography
							variant="h6"
							gutterBottom
							color="success.main"
						>
							Tack för din förfrågan!
						</Typography>
						<Typography variant="body1" gutterBottom>
							Vi kommer att kontakta dig inom kort för att boka en
							demo av Socrates.
						</Typography>
						<Divider sx={{ my: 2 }} />
						<Typography variant="body2" color="text.secondary">
							Du kan också kontakta oss direkt:
						</Typography>
						<Box
							mt={2}
							display="flex"
							flexDirection="column"
							gap={1}
						>
							<Box display="flex" alignItems="center" gap={1}>
								<EmailIcon color="primary" fontSize="small" />
								<a href="mailto:marknad@socrates.se">
									marknad@socrates.se
								</a>
							</Box>
							<Box display="flex" alignItems="center" gap={1}>
								<PhoneIcon color="primary" fontSize="small" />
								<a href="tel:+46036307900">036-30 79 00</a>
							</Box>
						</Box>
					</Box>
				</DialogContent>
			) : (
				<>
					<DialogContent>
						<Box display="flex" alignItems="top" gap={2}>
							<Typography
								variant="body1"
								gutterBottom
								sx={{ mb: 2 }}
							>
								Fyll i formuläret nedan för att boka en
								kostnadsfri demo av Socrates. Vårt team kommer
								att kontakta dig för att planera en presentation
								anpassad efter dina behov.
							</Typography>
							<Box
								component="img"
								src="/Socrates_ikon.png"
								alt="Socrates"
								sx={{
									width: 80,
									height: 80,
									objectFit: 'contain',
								}}
							/>
						</Box>
					</DialogContent>
					<DialogContent dividers>
						{errorMessage && (
							<Box mb={2}>
								<Alert variant="filled" severity="error">
									{errorMessage}
								</Alert>
							</Box>
						)}

						<FormGroup>
							{fields.map((field, index) => (
								<Box key={index} mb={2}>
									{!field.checkbox && (
										<TextField
											onChange={(e) =>
												changeField(
													index,
													e.target.value
												)
											}
											onBlur={() =>
												handleFieldBlur(index)
											}
											value={field.value}
											variant="outlined"
											size="medium"
											required={field.required}
											fullWidth
											label={field.label}
											type={field.type}
											error={!!fieldErrors[index]}
											helperText={fieldErrors[index]}
											disabled={loading}
										/>
									)}
									{field.checkbox && (
										<FormControlLabel
											key={'B' + index}
											control={
												<Checkbox
													checked={field.value}
													onChange={(event) =>
														changeField(
															index,
															event.target.checked
														)
													}
													disabled={loading}
												/>
											}
											label={field.label}
										/>
									)}
								</Box>
							))}
						</FormGroup>
					</DialogContent>
				</>
			)}
			<DialogActions sx={{ p: 2, gap: 1 }}>
				{sent ? (
					<Button
						variant="outlined"
						onClick={onClose}
						sx={{ minWidth: 100 }}
					>
						Stäng
					</Button>
				) : (
					<>
						<Button
							onClick={handleClose}
							disabled={loading}
							sx={{ minWidth: 100 }}
						>
							Avbryt
						</Button>
						<Button
							variant="contained"
							onClick={onSubmit}
							disabled={loading}
							sx={{
								minWidth: 120,
								position: 'relative',
							}}
						>
							{loading ? (
								<>
									<CircularProgress
										size={20}
										sx={{ mr: 1 }}
									/>
									Skickar...
								</>
							) : (
								'Skicka förfrågan'
							)}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}

SocratesOfferDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
};

export default SocratesOfferDialog;
